import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { GET_DASHBOARD_V2_STATIONS } from 'actions/dashboardV2';
import Card from 'components/inputs/Card';
import Loader from 'components/general/Loader';
import { RANGE_LABEL_STYLE, getRangeLabel } from './dateRanges';
import { useWidgetPermissions, V2_WIDGET_KEYS } from './widgetPermissions';

// Station Performance periods -> equivalent date-range key used for the grey caption.
const STATION_PERIODS = [
  { value: 'daily', label: 'Yesterday', rangeKey: 'yesterday' },
  { value: 'weekly', label: 'Last 7 Days', rangeKey: '7d' },
  { value: 'monthly', label: 'Last 30 Days', rangeKey: '30d' },
];

const COMBOS = [
  { type: 'top', category: 'revenue', title: 'Top 10 — Highest Revenue', permission: V2_WIDGET_KEYS.topStationsRevenue },
  {
    type: 'top',
    category: 'utilization',
    title: 'Top 10 — Highest Utilization',
    permission: V2_WIDGET_KEYS.topStationsUtilization,
  },
  { type: 'worst', category: 'revenue', title: 'Worst 10 — Lowest Revenue', permission: V2_WIDGET_KEYS.worstStationsRevenue },
  {
    type: 'worst',
    category: 'utilization',
    title: 'Worst 10 — Lowest Utilization',
    permission: V2_WIDGET_KEYS.worstStationsUtilization,
  },
];

const StationTable = ({ title, rows = [], isLoading }) => (
  <Card className="h-100 w-100">
    <h6 style={{ fontWeight: 600, marginBottom: 12 }}>{title}</h6>
    {isLoading ? (
      <div style={{ minHeight: 200, position: 'relative' }}>
        <Loader />
      </div>
    ) : (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#be210b' }}>
              {['Station', 'Revenue (₹)', 'Sessions', 'Utilization %', 'Downtime %'].map((h) => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#fff' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 16, textAlign: 'center', color: '#888' }}>
                  No data
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={idx}
                  style={{ borderBottom: '1px solid #2a3a47' }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(190,33,11,0.1)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '9px 12px' }}>{row.station}</td>
                  <td style={{ padding: '9px 12px' }}>{row.revenue}</td>
                  <td style={{ padding: '9px 12px' }}>{row.sessions}</td>
                  <td style={{ padding: '9px 12px' }}>{row.utilizationPct}%</td>
                  <td style={{ padding: '9px 12px' }}>{row.downtimePct}%</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);

const StationTables = () => {
  const dispatch = useDispatch();
  const { stations, isStationsLoading } = useSelector((state) => state.dashboardV2);
  const [period, setPeriod] = useState('daily');

  // CDB-494 — each table is a child module under Dashboardv2, toggled in Configure Role.
  // Only the tables this role can see are requested, so a hidden table costs no API call.
  const canSeeWidget = useWidgetPermissions();
  const visibleCombos = COMBOS.filter(({ permission }) => canSeeWidget(permission));
  const visibleKeys = visibleCombos.map(({ type, category }) => `${type}_${category}`).join(',');

  const fetchAll = (p) => {
    visibleCombos.forEach(({ type, category }) => {
      dispatch({ type: GET_DASHBOARD_V2_STATIONS.REQUEST, payload: { period: p, type, category } });
    });
  };

  useEffect(() => {
    fetchAll(period);
    // visibleKeys, not visibleCombos: the array identity changes on every render.
  }, [period, visibleKeys]);

  if (!visibleCombos.length) {
    return null;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600 }}>Station Performance</span>
        <span style={RANGE_LABEL_STYLE}>{getRangeLabel(STATION_PERIODS.find((p) => p.value === period)?.rangeKey)}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {STATION_PERIODS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setPeriod(value)}
              style={{
                padding: '4px 14px',
                borderRadius: 4,
                border: `1px solid ${period === value ? '#be210b' : '#3b4a56'}`,
                background: period === value ? '#be210b' : 'transparent',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: period === value ? 600 : 400,
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <Row className="d-flex align-items-stretch">
        {visibleCombos.map(({ type, category, title }) => (
          <Col key={`${type}_${category}`} md={visibleCombos.length === 1 ? 12 : 6} className="d-flex" style={{ marginBottom: 12 }}>
            <StationTable title={title} rows={stations[`${type}_${category}`]} isLoading={isStationsLoading} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

StationTable.propTypes = {
  title: PropTypes.string,
  rows: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default StationTables;
